import { TitleCasePipe } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KeyValuePair } from 'src/app/core/models/globals';
import { InfraCredential } from 'src/app/core/models/infraCredential';
import { ConfigEntityService } from 'src/app/core/services/config-entity.service';
import { StorageService } from 'src/app/core/services/storage.service';

@Component({
  selector: 'app-new-storage',
  templateUrl: './new-storage.component.html',
  styleUrls: ['./new-storage.component.scss']
})
export class NewStorageComponent implements OnInit {

  toAdd: boolean = this.data.toAdd;
  type = this.data.type?.value.toString() || -1;
  cloudName = this.data.type?.name.toString() || "";
  formGroup: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.pattern(/^[a-z0-9]+$/)]),
    fileShareName: new FormControl('syncstore', [Validators.pattern(/^[a-z0-9]+$/)]),
    type: new FormControl('bucket', [Validators.required]),
    deployment: new FormControl(''),
    location: new FormControl(null),
    storageClass: new FormControl(null, []),
    size: new FormControl('1', [Validators.pattern(/^[1-9]*$/)]),
    serviceTier: new FormControl(null),
    network: new FormControl('default'),
    credential: new FormControl(''),
    description: new FormControl(''),
    mountDirectory: new FormControl('/mnt/')
  });

  storageFields: any;
  storageTypes: KeyValuePair[] = [];
  deploymentNames: string[] = this.data.deploymentNames || [];
  locations: string[] = [];
  credentials: InfraCredential[] = [];

  _dialog = inject(MatDialogRef);
  _storageService = inject(StorageService);
  _configService = inject(ConfigEntityService);
  _snackBar = inject(MatSnackBar);
  _capitalize = inject(TitleCasePipe)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { toAdd: boolean, type: any, storageName?: string, deploymentNames: string[] },
  ) { }

  ngOnInit(): void {
    if(this.toAdd){
      this.getStorageJson();
      this.getCredentials();
      this.formGroup.controls['deployment'].setValue(this.deploymentNames[0]);
    } else {
      this.getStorageData();
    }
    this.formGroup.get("type").valueChanges.subscribe((value) => {
      if(value == "bucket"){
        this.formGroup.controls['mountDirectory'].clearValidators();
        this.formGroup.controls['mountDirectory'].setValue("/mnt/");
      } else {
        this.formGroup.controls['mountDirectory'].setValidators([Validators.pattern(/^\/mnt\/[a-z0-9]+(?:\/[a-z0-9]+)*$/)]);
        this.formGroup.controls['mountDirectory'].setValue("/mnt/" + this.formGroup.value.name);
      }
      this.formGroup.controls['mountDirectory'].updateValueAndValidity();
    })
  }

  getStorageJson(){
    this._storageService.getStorageJson(this.type).subscribe({
      next: (data) => {
        this.storageFields = data;
        this.storageTypes = Object.keys(this.storageFields).map(s => {return {key: this._capitalize.transform(s), value: s}});
        this.formGroup.controls['type'].setValue(this.storageTypes[0].value);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  getStorageData(){
    this._storageService.getStorageData(this.data.storageName).subscribe({
      next: (data: any) => {
        if(data.type == "bucket") {
          this._snackBar.open("Storage type is not supported for editing." , "Close", { duration: 3000 });
          this._dialog.close(false);
        }
        this.formGroup.controls['name'].setValue(data.name);
        this.formGroup.controls['fileShareName'].setValue(data?.fileShareName || "");
        this.formGroup.controls['type'].setValue(data?.type || "");
        this.formGroup.controls['deployment'].setValue(data?.deployment || "");
        this.formGroup.controls['location'].setValue(data?.location || "");
        this.formGroup.controls['storageClass'].setValue(data?.storageClass || "");
        this.formGroup.controls['size'].setValue(data?.totalStorage || "0");
        this.formGroup.controls['serviceTier'].setValue(data?.serviceTier || "");
        this.formGroup.controls['network'].setValue(data?.network || "");
        this.formGroup.controls['credential'].setValue(data?.credential || "");
        this.formGroup.controls['description'].setValue(data?.description || "");

        // disable all fields except size
        this.formGroup.controls['name'].disable();
        this.formGroup.controls['fileShareName'].disable();
        this.formGroup.controls['type'].disable();
        this.formGroup.controls['deployment'].disable();
        this.formGroup.controls['location'].disable();
        this.formGroup.controls['storageClass'].disable();
        this.formGroup.controls['serviceTier'].disable();
        this.formGroup.controls['network'].disable();
        this.formGroup.controls['credential'].disable();
      },
      error: (error) => {
        console.error(error);
      }
    })
  }

  getCredentials(){
    this._configService.getAllInfraCredentials().subscribe({
      next: (data) => {
        this.credentials = JSON.parse(JSON.stringify(data)).filter(c => c.infrastructure == this.cloudName);
        if(this.credentials.length == 0){
          this._snackBar.open("No credentials found for this cloud provider." , "Close", { duration: 3000 });
          this._dialog.close(false);  
        }
        this.formGroup.controls['credential'].setValue(this.credentials[0].friendlyName);
      },
      error: (error) => {
        console.error(error);
      }
    })
  }

  onSubmit(){
    if(this.formGroup.status == 'INVALID'){
      this.formGroup.markAllAsTouched();
    } else {
      const storageData = Object.entries(this.formGroup.getRawValue()).map(([key, value]) => ({ depKey: key, depValue: value }));
      if(this.toAdd){
        this._storageService.createStorage(storageData, this.type).subscribe({
          next: (data) => {
            this._snackBar.open("Storage creation in progress, check status for further information." , "Close", { duration: 3000 });
            this._dialog.close(true);
          },
          error: (error) => {
            this._snackBar.open(error?.error?.message || "Error creating storage." , "Close", { duration: 3000 });
          }
        })
      } else {
        this._storageService.updateStorage(storageData, this.type).subscribe({
          next: (data) => {
            this._snackBar.open("Storage update in progress, check status for further information." , "Close", { duration: 5000 });
            this._dialog.close(true);
          },
          error: (error) => {
            this._snackBar.open(error?.error?.message || "Error updating storage." , "Close", { duration: 3000 });
          }
        })
      }
    }
  }

  onClose() {
    this._dialog.close(false)
  }

  getStorageClass(){
    if(!this.storageFields) return [];
    const storageClass = this.storageFields['bucket'].find(s => s.name == "storageClass").allowedValues.split(",");
    if(!this.formGroup.value.storageClass) this.formGroup.controls['storageClass'].setValue(storageClass[0]);
    return storageClass;
  }

  getServiceTier(){
    if(!this.storageFields) return [];
    const serviceTiers = this.storageFields['fileStore'].find(s => s.name == "serviceTier").allowedValues.split(",");
    if(!this.formGroup.value.serviceTier) this.formGroup.controls['serviceTier'].setValue(serviceTiers[0]);
    return serviceTiers;
  }

  getLocations(){
    if(!this.storageFields) return [];
    const locationField = this.storageFields[this.formGroup.value.type].find(s => s.name == "location");
    const locations = locationField.allowedValues.split(",");
    locations.unshift(locationField.defaultValue);
    if(!locations.includes(this.formGroup.value.location)) this.formGroup.controls['location'].setValue(null);
    if(!this.formGroup.value.location) this.formGroup.controls['location'].setValue(locations[0]);
    return locations;
  }
}
